import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Alert,
    LinearProgress,
    TextField,
    InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { Certificate, certificateService } from '../../services/certificateService';
import CertificateCard from './CertificateCard';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
};

const MyCertificates: React.FC = () => {
    const [certificates, setCertificates] = useState<Certificate[]>([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');

    const translate = useTranslate();

    useEffect(() => {
        loadCertificates();
    }, []);

    const loadCertificates = async () => {
        setLoading(true);
        try {
            const data = await certificateService.getMyCertificates();
            setCertificates(data);
        } catch (error) {
            console.error('Error loading certificates:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterCertificates = (filter?: 'valid' | 'expired') => {
        let filtered = certificates;

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (cert) =>
                    cert.courseName.toLowerCase().includes(query) ||
                    cert.verificationCode.toLowerCase().includes(query)
            );
        }

        // Apply status filter
        if (filter === 'valid') {
            filtered = filtered.filter((cert) => cert.isValid);
        } else if (filter === 'expired') {
            filtered = filtered.filter((cert) => cert.isExpired);
        }

        return filtered;
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>{translate('certificates.loading')}</Typography>
                <LinearProgress sx={{ mt: 2 }} />
            </Box>
        );
    }

    const allCertificates = filterCertificates();
    const validCertificates = filterCertificates('valid');
    const expiredCertificates = filterCertificates('expired');

    const renderCertificateGrid = (certs: Certificate[]) => {
        if (certs.length === 0) {
            return (
                <Alert severity="info">
                    {translate('certificates.noCertificates')}
                </Alert>
            );
        }

        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                    gap: 3
                }}
            >
                {certs.map((certificate) => (
                    <CertificateCard key={certificate.id} certificate={certificate} />
                ))}
            </Box>
        );
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <TextField
                    size="small"
                    placeholder={translate('certificates.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                    sx={{ width: 300 }}
                />
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab
                        label={translate('certificates.tabs.all', { count: allCertificates.length })}
                    />
                    <Tab
                        label={translate('certificates.tabs.valid', { count: validCertificates.length })}
                    />
                    <Tab
                        label={translate('certificates.tabs.expired', { count: expiredCertificates.length })}
                    />
                </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
                {renderCertificateGrid(allCertificates)}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
                {renderCertificateGrid(validCertificates)}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
                {renderCertificateGrid(expiredCertificates)}
            </TabPanel>
        </Box>
    );
};

export default MyCertificates;
